import {
    DiagnosisEntityOrSymptomsEntity,
    SelectionEntity,
} from '../../../../../store/types/prescription';
import AntAutoCompleteV2 from './AntAutoComplete';
import { generateLocalId } from '../../../../../store/utils';
import { PropertyGroupEntity } from '../../../../../api/fetchSymDiaProperties';
import FreeText from './FreeText';
import AntSelectWithLabel from './AntSelectWithLabel';
import { generateOptionsFunc } from '../generateOptionsFunc';
import AntSelect from './AntSelect';
import { track } from '../../../../../tracking';
import { DIAGNOSIS_KIND, SYMPTOMS_KIND } from '../../../trackSource';
import { EditableCell, EditableCellV2 } from './MedTableRenderer';
import { Fragment, useState } from 'react';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';
import TinyMCEditor from '../../../../common/TinyMceEditor';
import { filterParsedText } from '../../previewScreen/Advices';
import TinyMceEditorModal from '../../../../common/TinyMceEditorModal';
import { AntTooltip } from '../../../../common/AntTooltip';

type P = {
    editableDiv?: boolean;
    data: DiagnosisEntityOrSymptomsEntity;
    p: PropertyGroupEntity;
    update: (data: DiagnosisEntityOrSymptomsEntity) => void;
    setRef?: (ref: EkaFocosableElement) => void;
    withLabel?: boolean;
    containerId?: string;
    kind: typeof SYMPTOMS_KIND | typeof DIAGNOSIS_KIND;
};

const updateSelection = (
    data: DiagnosisEntityOrSymptomsEntity,
    p: PropertyGroupEntity,
    selection: SelectionEntity[],
    kind: typeof SYMPTOMS_KIND | typeof DIAGNOSIS_KIND,
): DiagnosisEntityOrSymptomsEntity => {
    const updatedData = {
        ...data,
        properties: {
            ...(data.properties || {}),
            [p.id]: {
                name: p.name,
                selection: [...selection],
            },
        },
    };
    track('PX_ELEMENT_PROPERTY', {
        // eId: data.id,
        eName: data.name,
        // pId: p.id,
        pName: p.name,
        action: selection.length > 0 && selection[0].value ? 'SET' : 'UNSET',
        kind: kind,
    });
    return updatedData;
};

const SymDiaPropertyInput: React.FC<P> = ({
    data,
    p,
    update,
    setRef,
    withLabel,
    kind,
    editableDiv,
    containerId,
}) => {
    const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [editorId, setEditorId] = useState<string>('');

    const currentProperty = data.properties?.[p.id];
    const firstSelection =
        (currentProperty &&
            currentProperty.selection &&
            currentProperty.selection.length > 0 &&
            currentProperty.selection[0]) ||
        undefined;

    const firstSelectionValue = firstSelection && firstSelection.value;
    switch (p.type) {
        case 'open-text-box':
            const uniqueEditorId = `${data.id}-${p.id}-editor`;

            return (
                <>
                    {withLabel && (
                        <div
                            className="flex"
                            style={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                paddingInline: '5px',
                            }}
                        >
                            <div className="font-700 text-12 text-neutral-3">{p.name}</div>
                            <div className="flex items-center justify-center w-12 h-12">
                                <AntTooltip content="Formatted text visible only in bulleted format in Rx.">
                                    <svg
                                        className="w-12 h-12 fill-neutral-3"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                    >
                                        <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 464c-114.7 0-208-93.31-208-208S141.3 48 256 48s208 93.31 208 208S370.7 464 256 464zM296 336h-16V248C280 234.8 269.3 224 256 224H224C210.8 224 200 234.8 200 248S210.8 272 224 272h8v64h-16C202.8 336 192 346.8 192 360S202.8 384 216 384h80c13.25 0 24-10.75 24-24S309.3 336 296 336zM256 192c17.67 0 32-14.33 32-32c0-17.67-14.33-32-32-32S224 142.3 224 160C224 177.7 238.3 192 256 192z"></path>
                                    </svg>
                                </AntTooltip>
                            </div>
                        </div>
                    )}
                    {p?.id !== 'pg-5243973452' && (
                        <FreeText
                            setRef={setRef}
                            placeholder={p.name}
                            label={p.name}
                            defaultValue={firstSelectionValue || ''}
                            onBlur={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                update(
                                    updateSelection(
                                        data,
                                        p,
                                        [
                                            {
                                                id:
                                                    (p.properties &&
                                                        p.properties[0] &&
                                                        p.properties[0].id) ||
                                                    generateLocalId(),
                                                value: e.target.value,
                                            },
                                        ],
                                        kind,
                                    ),
                                );
                            }}
                        />
                    )}
                    {p?.id === 'pg-5243973452' && (
                        <TinyMCEditor
                            //TODO dont use unique id try a general one
                            id={uniqueEditorId}
                            initialValue={firstSelectionValue || ''}
                            onBlur={(text: string, parsedText: string) => {
                                if (setRef) {
                                    const dummyRef: EkaFocosableElement = {
                                        focus: () => {
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            tinymce?.get(uniqueEditorId)?.focus();
                                        },
                                        blur: () => {
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            tinymce?.get(uniqueEditorId)?.blur();
                                        },
                                        //currently using text(HTML) keeping parsed text so it could be changed to string later if needed
                                        innerText: text,
                                    };
                                    setRef(dummyRef);
                                }

                                update(
                                    updateSelection(
                                        data,
                                        p,
                                        [
                                            {
                                                id:
                                                    (p.properties &&
                                                        p.properties[0] &&
                                                        p.properties[0].id) ||
                                                    generateLocalId(),
                                                //value: filterParsedText(parsedText),
                                                value: text,
                                            },
                                        ],
                                        kind,
                                    ),
                                );
                            }}
                            customOptions={{
                                inline: false,
                                height: 80,
                            }}
                            isFullScreen={true}
                            fullScreenHeightCssClass={200}
                            parentCssClasses="relative flex flex-col tiny-mce overflow-auto rounded-4 hide-scrollbar min-h-96"
                            toolbarId={`${data.id}-${p.id}-toolbar`}
                            onFullScreenToggle={() => {
                                // Get current content from the editor
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                const content = tinymce?.get(uniqueEditorId)?.getContent() || '';
                                setModalContent(content);
                                setEditorId(uniqueEditorId);
                                setIsEditorModalOpen(true);
                            }}
                        />
                    )}

                    {isEditorModalOpen && (
                        <TinyMceEditorModal
                            initialContent={modalContent}
                            onSave={(htmlContent, textContent) => {
                                console.log('Parent received HTML content:', htmlContent);
                                console.log('Parent received text content:', textContent);

                                try {
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    const editor = tinymce?.get(editorId);
                                    if (editor) {
                                        console.log(editorId);
                                        editor.setContent(htmlContent);
                                    } else {
                                        console.error('Could not find editor with ID:', editorId);
                                    }

                                    // Step 2: Process the parsed content
                                    const parsedValue = filterParsedText(textContent);

                                    // Step 3: Create the selection entity
                                    const selectionEntity = {
                                        id:
                                            (p.properties &&
                                                p.properties[0] &&
                                                p.properties[0].id) ||
                                            generateLocalId(),
                                        //setting value to parsed TODO: check for the format later
                                        value: htmlContent,
                                    };

                                    // Step 4: Call the update function with the new selection
                                    const updatedData = updateSelection(
                                        data,
                                        p,
                                        [selectionEntity],
                                        kind,
                                    );

                                    update(updatedData);
                                } catch (error) {
                                    console.error('Error in parent saving content:', error);
                                }
                            }}
                            onClose={() => {
                                setIsEditorModalOpen(false);
                            }}
                        />
                    )}
                </>
            );

        case 'radio': {
            // This check needs to always be in sync with setRef of child
            const Wrapper = editableDiv ? EditableCellV2 : Fragment;

            let options = p.properties || [];
            if (firstSelection?.id && !options.find((o) => o.id === firstSelection?.id)) {
                options = [{ id: firstSelection.id, name: firstSelection.value }, ...options];
            }

            const child = (
                <AntSelect
                    containerIdToAttachDropdown={containerId}
                    showArrow={!editableDiv}
                    defaultOpen={editableDiv}
                    setRef={editableDiv ? undefined : setRef}
                    className="w-full"
                    autoFocus={editableDiv}
                    allowClear
                    placeholder={p.name}
                    optionFilterProp="children"
                    options={options || ([] as { id: string; name: string }[])}
                    getLabelToCompare={(o) => o.name}
                    setValue={(option) =>
                        update(
                            updateSelection(
                                data,
                                p,
                                option ? [{ id: option.id, value: option.name }] : [],
                                kind,
                            ),
                        )
                    }
                    filterOption={true}
                    value={
                        firstSelection
                            ? { id: firstSelection.id, name: firstSelection.value }
                            : ({} as { id: string; name: string })
                    }
                    getValue={(t) => t && t.name}
                />
            );

            const Comp = (
                <Wrapper defaultValue={firstSelection?.value || ''} label={p.name} setRef={setRef}>
                    {child}
                </Wrapper>
            );
            return p.properties && p.properties.length > 0 ? (
                <div>
                    {withLabel && (
                        <div>
                            <p className="font-700 text-12 text-neutral-3">{p.name}</p>
                        </div>
                    )}
                    {Comp}
                </div>
            ) : null;
        }
        case 'suggest':
            let suggestOptions = p.properties || [];
            if (firstSelection?.id && !suggestOptions.find((o) => o.id === firstSelection?.id)) {
                suggestOptions = [
                    { id: firstSelection.id, name: firstSelection.value },
                    ...suggestOptions,
                ];
            }

            return (
                <AntSelectWithLabel
                    containerIdToAttachDropdown={containerId}
                    setRef={setRef}
                    label={p.name}
                    allowClear
                    value={
                        firstSelection
                            ? { id: firstSelection.id, n: firstSelection.value }
                            : undefined
                    }
                    placeholder={`Select ${p.name}`}
                    options={suggestOptions.map((pg) => ({ id: pg.id, n: pg.name })) || []}
                    getValue={(t) => t.id}
                    getLabelToCompare={(t) => t.n}
                    setValue={(t) =>
                        update(updateSelection(data, p, t ? [{ id: t.id, value: t.n }] : [], kind))
                    }
                    showSearch={true}
                    optionFilterProp="n"
                    filterOption={true}
                />
            );
        case 'prefix-suggest':
            if (!p.properties || p.properties.length === 0) {
                return null;
            }
            const value = firstSelection;

            const onSearch = async (
                text: string,
            ): Promise<
                { id: string; label: string; value: string; meta?: { id: string; unit: string } }[]
            > => {
                return new Promise((resolve) => {
                    resolve(generateOptionsFunc(p.properties || [])(text));
                });
            };

            const Comp = withLabel ? AntAutoCompleteV2 : EditableCell;

            return (
                <div>
                    {withLabel && (
                        <div>
                            <p className="font-700 text-12 text-neutral-3">{p.name}</p>
                        </div>
                    )}
                    <Comp
                        containerIdToAttachDropdown={containerId}
                        label={p.name}
                        fieldName={`sdp-${data.id}-${p.id}`}
                        withoutError
                        setRef={setRef}
                        onSearch={onSearch}
                        debounceDelay={0}
                        selectedValue={
                            value
                                ? {
                                      id: value.value,
                                      label: `${value.value} ${value.unit || ''}`,
                                      value: value.value,
                                  }
                                : undefined
                        }
                        getLabelToCompare={(o) => o.label}
                        placeholder={p.name}
                        onSelect={(option) => {
                            update(
                                updateSelection(
                                    data,
                                    p,
                                    option
                                        ? [
                                              {
                                                  id: option.id,
                                                  value: option.value,
                                                  unit: option?.meta?.unit,
                                              },
                                          ]
                                        : [],
                                    kind,
                                ),
                            );
                        }}
                    />
                </div>
            );
        case 'multi-select-suggest':
            if (!p.properties || p.properties.length === 0) {
                return null;
            }

            let multiSelectOptions = p.properties;
            if (currentProperty?.selection) {
                for (let index = 0; index < currentProperty?.selection.length; index++) {
                    const selection = currentProperty?.selection[index];
                    if (!multiSelectOptions.find((t) => t.id === selection.id)) {
                        multiSelectOptions = [
                            { id: selection.id, name: selection.value },
                            ...multiSelectOptions,
                        ];
                    }
                }
            }

            return (
                <AntSelectWithLabel
                    containerIdToAttachDropdown={containerId}
                    setRef={setRef}
                    label={`Select ${p.name}`}
                    className="w-full"
                    isMulti={true}
                    showSearch={true}
                    allowClear
                    placeholder={`Select ${p.name}`}
                    value={
                        currentProperty?.selection?.map((t) => ({ id: t.id, name: t.value })) || []
                    }
                    notFoundContent={null}
                    options={multiSelectOptions}
                    getValue={(o) => o?.id}
                    getLabelToCompare={(o) => o.name}
                    setValue={(t) =>
                        update(
                            updateSelection(
                                data,
                                p,
                                t?.map((item) => ({ id: item.id, value: item.name })) || [],
                                kind,
                            ),
                        )
                    }
                    filterOption={true}
                    optionFilterProp="name"
                />
            );

        default:
            return null;
    }
};

export default SymDiaPropertyInput;
